import { h } from 'preact';
import { useState, useRef } from 'preact/hooks';
import Layout from './layout';

const getSizedImageUrl = (src, size) => {
  if (size === null) return src;
  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|jpg.webp|gif.webp|png.webp)(\?v=\d+)?$/i);
  if (!match) return null;
  const prefix = src.split(match[0]);
  const suffix = match[0];
  return `${prefix[0]}_${size}x${suffix}`.replace(/http(s)?:/, '');
};

const Image = ({
  alt = '',
  src,
  srcWidths = {},
  height = '',
  width = '',
  classes = '',
  zoom = false,
  loading = 'lazy',
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const zoomRef = useRef(null);

  const handleMouseMove = (e) => {
    if (isZoomed && zoomRef.current) {
      // eslint-disable-next-line no-shadow
      const { left, top, width, height } = zoomRef.current.getBoundingClientRect();
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;
      setPosition({ x, y });
    }
  };

  const handleClick = (e) => {
    if (zoomRef.current) {
      // eslint-disable-next-line no-shadow
      const { left, top, width, height } = zoomRef.current.getBoundingClientRect();
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;
      setPosition({ x, y });
    }
    setIsZoomed(!isZoomed);
  };

  const handleMouseLeave = () => {
    if (isZoomed) {
      setIsZoomed(false);
    }
  };

  let imageSrc = src;
  let imageClasses = classes;

  if (!imageSrc) {
    let generalData = document.querySelector('[data-general]');
    if (!generalData) {
      imageSrc = '';
    } else {
      generalData = JSON.parse(generalData.textContent);
      const { placeholderImage } = generalData;
      imageSrc = placeholderImage.src;
      imageClasses = 'w-full h-full object-cover';
    }
  }

  const bps = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };
  const widths = srcWidths;

  Object.keys(bps).forEach((key) => !widths[key] ?? delete bps[key]);
  Object.keys(widths).forEach((key) => !bps[key] ?? delete widths[key]);

  const srcWidthsReduced = Object.values(widths);
  const sizeWidthsReduced = Object.values(bps);

  const srcsetFormatted =
    srcWidthsReduced && srcWidthsReduced.map((w) => `${getSizedImageUrl(imageSrc, w)} ${w}w`).join(',');
  const sizesFormatted =
    srcWidthsReduced &&
    `${srcWidthsReduced
      .map((w, i) => `(max-width: ${sizeWidthsReduced[i]}px) ${w}px`)
      .slice(0, srcWidthsReduced.length - 1)
      .join(',')}, ${srcWidthsReduced[srcWidthsReduced.length - 1]}px`;

  const imageComponent = (
    <img
      className={`image ${imageClasses} ${zoom ? 'transition-transform duration-300' : ''}`}
      style={
        zoom ? { transformOrigin: `${position.x}% ${position.y}%`, transform: isZoomed ? 'scale(2)' : 'scale(1)' } : {}
      }
      srcSet={srcsetFormatted}
      sizes={sizesFormatted}
      src={imageSrc}
      alt={alt}
      height={height}
      width={width}
      loading={loading}
    />
  );

  if (zoom && Layout.getInstance().isAboveBreakpoint('md')) {
    return (
      <div
        ref={zoom ? zoomRef : null}
        className={`relative overflow-hidden ${zoom ? (isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in') : ''}`}
        onMouseMove={zoom ? handleMouseMove : null}
        onClick={zoom ? handleClick : null}
        onMouseLeave={zoom ? handleMouseLeave : null}
      >
        {imageComponent}
      </div>
    );
  }

  return imageComponent;
};

export default Image;
